import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import AuthShell from '../components/auth/AuthShell';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import TermsContent from '../components/TermsContent';

const intervalLabel = (i) => (i === 'month' ? '/mo' : i === 'year' ? '/yr' : '');
const priceLabel = (p) => (Number(p.price || 0) > 0 ? `$${p.price}${intervalLabel(p.interval)}` : 'Free');
const FREE_PLAN = { id: 'basic', name: 'Seekers Community Membership', tagline: 'Free forever — join the sangha', price: 0, benefits: [] };

const Signup = () => {
  const { signup, googleLogin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([FREE_PLAN]);
  const [selectedPlanId, setSelectedPlanId] = useState(params.get('plan') || 'basic');

  useEffect(() => {
    Promise.all([
      api.get('/memberships/plans').then((r) => r.data || []).catch(() => []),
      api.get('/memberships/basic').then((r) => r.data).catch(() => null),
    ]).then(([paid, basic]) => {
      const free = basic ? { ...FREE_PLAN, name: basic.name, tagline: basic.tagline, benefits: basic.benefits || [] } : FREE_PLAN;
      setPlans([free, ...paid]);
      const wanted = params.get('plan');
      if (wanted && wanted !== 'basic' && !paid.some((p) => p.id === wanted)) setSelectedPlanId('basic');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || FREE_PLAN;
  const isPaid = selectedPlan.id !== 'basic' && Number(selectedPlan.price || 0) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast({ title: 'Password too short', description: 'Please use at least 6 characters.' });
      return;
    }
    if (!form.phone.trim()) {
      toast({ title: 'Phone number required', description: 'Please enter your phone number.' });
      return;
    }
    if (!agreed) {
      toast({ title: 'Please agree to continue', description: 'You must accept the Terms & Conditions and Privacy Policy.' });
      return;
    }
    setLoading(true);
    try {
      await signup({ ...form, terms_accepted: true, sms_consent: true });
      if (isPaid) {
        try {
          const res = await api.post('/payments/checkout', { plan_id: selectedPlan.id, origin_url: window.location.origin });
          window.location.href = res.data.checkout_url;
          return;
        } catch (err) {
          toast({ title: 'Account created', description: 'We could not start checkout — you can pick a plan from your dashboard.' });
          navigate('/membership', { replace: true });
          return;
        }
      }
      toast({ title: 'Welcome to Osho Universal 🌸', description: 'Your account is ready.' });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Signup failed';
      toast({ title: 'Signup failed', description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create Your Account"
      subtitle="Join our global community of seekers."
      footer={<>Already a member? <Link to="/login" className="text-mahogany font-medium hover:underline">Sign in</Link></>}
    >
      <div className="mb-6" data-testid="signup-plan-picker">
        <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Choose your plan</span>
        <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
          <SelectTrigger className="w-full bg-white/70 border-rose/50 rounded-2xl py-6 text-burgundy" data-testid="signup-plan-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-cream border-rose/40">
            {plans.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`signup-plan-opt-${p.id}`}>
                <span className="flex items-center gap-2">
                  <span className="font-medium text-burgundy">{p.name}</span>
                  <span className="text-mahogany/60">· {priceLabel(p)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Benefits of the selected plan */}
        <div className="mt-3 rounded-2xl bg-rose-soft/30 border border-rose/40 p-4" data-testid="signup-plan-benefits">
          <div className="flex items-baseline justify-between">
            <div className="font-serif-display text-lg text-burgundy">{selectedPlan.name}</div>
            <div className="font-serif-display text-lg text-burgundy whitespace-nowrap">{priceLabel(selectedPlan)}</div>
          </div>
          {selectedPlan.tagline && <div className="text-xs text-mahogany/70">{selectedPlan.tagline}</div>}
          <ul className="mt-3 space-y-1.5">
            {(selectedPlan.benefits || []).map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-burgundy/85">
                <Check className="w-4 h-4 text-mahogany mt-0.5 shrink-0" /> <span>{b}</span>
              </li>
            ))}
            {(selectedPlan.benefits || []).length === 0 && <li className="text-sm text-mahogany/60">Full community access, free forever.</li>}
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Field label="Full Name" name="full_name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <Field label="Email" type="email" name="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <Field label="Phone" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Password (min 6 chars)" type="password" name="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />

        <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-terms-label">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="signup-terms-checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-mahogany"
          />
          <span className="text-sm text-burgundy/85 leading-snug">
            I have read and agree to the{' '}
            <Link to="/terms-and-conditions" target="_blank" className="text-mahogany font-medium hover:underline">Terms &amp; Conditions</Link>,
            {' '}the{' '}
            <Link to="/privacy-policy" target="_blank" className="text-mahogany font-medium hover:underline">Privacy Policy</Link>,
            {' '}and the messaging consent below.
          </span>
        </label>

        <div
          className="max-h-56 overflow-y-auto rounded-xl border border-rose/40 bg-white/70 p-4 text-mahogany/80"
          data-testid="signup-terms-scroll"
          tabIndex={0}
        >
          <TermsContent compact />

          <section className="mt-2">
            <h2 className="text-[13px] font-normal text-burgundy mb-0.5">19. Messaging Consent (SMS, WhatsApp &amp; Email)</h2>
            <div className="text-[11px] text-mahogany/70 leading-snug" data-testid="signup-sms-consent">
              <p>By providing your phone number and creating an account, you agree that <strong>Osho Universal – Meditative Mindfulness Inc</strong> may send you recurring automated marketing and informational <strong>text (SMS) and WhatsApp messages</strong> — including event &amp; retreat announcements and account/support messages — at the number provided, as well as email updates. Consent is not a condition of purchase. Message frequency varies. Msg &amp; data rates may apply. Reply <strong>STOP</strong> to opt out at any time, or <strong>HELP</strong> for help. See our{' '}
              <Link to="/terms-and-conditions" target="_blank" className="text-mahogany hover:underline">Terms</Link> &amp;{' '}
              <Link to="/privacy-policy" target="_blank" className="text-mahogany hover:underline">Privacy Policy</Link>.</p>
            </div>
          </section>
        </div>

        <button type="submit" disabled={loading || !agreed} className="pill-btn justify-center w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="signup-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {isPaid ? `Create Account & Continue to Payment` : 'Create Account'}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-mahogany/50 text-xs">
        <span className="flex-1 h-px bg-rose/50" /> OR <span className="flex-1 h-px bg-rose/50" />
      </div>

      <button onClick={googleLogin} className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-mahogany/25 hover:bg-rose-soft/40 transition-colors">
        <svg width="18" height="18" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.2 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 16.1 4 9.2 8.4 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2 14-5.3l-6.5-5.3C29.4 34.7 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9 39.5 15.9 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.5 5.3C40.9 36.6 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z" />
        </svg>
        <span className="text-burgundy font-medium">Sign up with Google</span>
      </button>
    </AuthShell>
  );
};

const Field = ({ label, type = 'text', name, value, onChange, required = true }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany transition-colors"
      required={required}
    />
  </label>
);

export default Signup;
